import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { merge, Observable, of } from 'rxjs';
import {
    catchError,
    map,
    switchMap,
    take
} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { ManagementApiService } from '../management-api.service';
import {ItemGroupingsActions} from '@emm/management/store/item-groupings/item-groupings.actions';
import {Item} from '@emm/core/models/item.model';
import {Node} from '@emm/core/models/node.model';

@Injectable({
    providedIn: 'root'
})
export class ItemGroupingsEffects {

    loadItemGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.loadItemGroups),
            switchMap(() =>
                this.api.getItemGroupings().pipe(
                    map((items) => ItemGroupingsActions.loadItemGroupsSuccess({ items })),
                    catchError(({ error }) => of(ItemGroupingsActions.loadItemGroupsFailure({ error })))
                )
            )
        )
    );

    loadAllChildItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.loadAllChildItems),
            switchMap(({ id }) =>
                this.api.getAllChildItems(id).pipe(
                    map((items) => ItemGroupingsActions.loadAllChildItemsSuccess({ items })),
                    catchError(({ error }) => of(ItemGroupingsActions.loadAllChildItemsFailure({ error })))
                )
            )
        )
    );

    toggleDisabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.toggleDisabled),
            switchMap(({ tag, enabled }) =>
                this.api.setEnabled(tag, enabled).pipe(
                    map((_) => ItemGroupingsActions.toggleDisabledSuccess()),
                    catchError(({ error }) => of(ItemGroupingsActions.toggleDisabledFailure({ error })))
                )
            )
        )
    );

    addChildItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.addChildItem),
            switchMap(({ parentTag, children, isGlobalAdd }) =>
                this.api.addChildItems(parentTag, children, isGlobalAdd).pipe(
                    map((_) => ItemGroupingsActions.addChildItemSuccess()),
                    catchError(({ error }) => of(ItemGroupingsActions.addChildItemFailure({ error })))
                )
            )
        )
    );

    itemGroupings$ = this.actions$.pipe(
        ofType(ItemGroupingsActions.loadItemGroupsSuccess),
        map((payload) => payload.items),
        map((items: Item[]) =>
            RA.isNonEmptyArray(items) ? items.map(ItemGroupingsEffects.mapItemGroupingsToNode) : []
        )
    );

    deleteItemGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.deleteItemGroup),
            switchMap(({ tag }) =>
                this.api.deleteItem(tag).pipe(
                    map((_) => ItemGroupingsActions.deleteItemGroupSuccess()),
                    catchError(({ error }) => of(ItemGroupingsActions.deleteItemGroupFailure({ error })))
                )
            )
        )
    );

    removeChildItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.removeChildItem),
            switchMap(({ parentTag, childTag }) =>
                this.api.removeChildItem(parentTag, childTag).pipe(
                    map((_) => ItemGroupingsActions.removeChildItemSuccess()),
                    catchError(({ error }) => of(ItemGroupingsActions.removeChildItemFailure({ error })))
                )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ItemGroupingsActions.deleteItemGroupSuccess),
            switchMap(() => of(ItemGroupingsActions.loadItemGroups()))
        )
    );

    static mapItemGroupingsToNode(item: Item): Node<Item> {
        return {
            data: item,
            hasChildren: item.parent,
            id: item.tag,
            name: item.name
        };
    }

    constructor(
        private actions$: Actions,
        private api: ManagementApiService
    ) {}



    getAllChildItems(): Observable<Node<Item>[]> {
        const success$ = this.actions$.pipe(
            ofType(ItemGroupingsActions.loadAllChildItemsSuccess),
            map((payload) => payload.items),
            map((items: Item[]) =>
                RA.isNonEmptyArray(items) ? items.map(ItemGroupingsEffects.mapItemGroupingsToNode) : []
            )
        );

        const failure$ = this.actions$.pipe(
            ofType(ItemGroupingsActions.loadAllChildItemsFailure),
            map(() => [])
        );

        return merge(success$, failure$).pipe(take(1));
    }



}
